import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { contact } from '../content_helper';

const FORM_SUBMIT_URL = `https://formsubmit.co/ajax/${encodeURIComponent(contact.email)}`;

const Contact = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const submitted = searchParams.get('sent') === '1';
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setSending(true);

    const form = event.currentTarget;
    const formData = new FormData(form);
    formData.append('_subject', 'Portfolio contact — new message');
    formData.append('_captcha', 'false');
    formData.append('_template', 'table');

    try {
      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });

      const result = await response.json();

      if (!response.ok || result.success === 'false') {
        throw new Error(result.message || `Submission failed (${response.status})`);
      }

      form.reset();
      navigate('/contact?sent=1');
    } catch {
      setError(
        `Could not send your message. Email me directly at ${contact.email}.`
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="pt-32 pb-stack-lg px-margin-page max-w-container-max mx-auto technical-grid min-h-screen">
        {/*  Hero Header  */}
        <div className="mb-16 max-w-3xl">
            <h1 className="font-display text-5xl md:text-6xl font-bold text-primary mb-6 leading-tight">Let's Build the
                Future Together</h1>
            <p className="font-body text-xl text-on-surface-variant leading-relaxed">Whether it's a complex architectural
                challenge or a visionary AI integration, I'm ready to bring technical precision to your next project.
            </p>
        </div>
        {/*  Bento Grid Contact Section  */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-gutter">
            {/*  Contact Form Container  */}
            <section className="lg:col-span-7 glass-card p-10 rounded-xl">
                <div className="flex items-center gap-4 mb-10">
                    <div className="p-3 bg-primary/10 rounded-lg">
                        <span className="material-symbols-outlined text-primary text-3xl">alternate_email</span>
                    </div>
                    <h2 className="font-display text-2xl font-bold text-on-surface">Secure Channel</h2>
                </div>
                {submitted && (
                    <p className="mb-8 p-4 rounded-lg border border-primary/30 bg-primary/10 text-primary font-body text-body-md">
                        Message sent successfully. I&apos;ll get back to you soon.
                    </p>
                )}
                {error && (
                    <p className="mb-8 p-4 rounded-lg border border-error/30 bg-error/10 text-error font-body text-body-md">
                        {error}
                    </p>
                )}
                <form className="space-y-8" onSubmit={handleSubmit} noValidate>
                    <input type="text" name="_honey" className="hidden" tabIndex={-1} autoComplete="off" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-stack-md">
                        <div className="space-y-3">
                            <label
                                htmlFor="contact-name"
                                className="font-label text-xs tracking-widest text-primary/70 uppercase font-bold">Identification
                                / Name</label>
                            <input
                                id="contact-name"
                                name="name"
                                required
                                className="w-full bg-surface-container-lowest/50 border border-outline-variant/30 focus:border-primary focus:ring-1 focus:ring-primary rounded-lg p-4 text-on-surface outline-none transition-all placeholder:text-on-surface-variant/40"
                                placeholder="e.g. John Doe"
                                type="text" />
                        </div>
                        <div className="space-y-3">
                            <label
                                htmlFor="contact-email"
                                className="font-label text-xs tracking-widest text-primary/70 uppercase font-bold">Return
                                Address / Email</label>
                            <input
                                id="contact-email"
                                name="email"
                                required
                                className="w-full bg-surface-container-lowest/50 border border-outline-variant/30 focus:border-primary focus:ring-1 focus:ring-primary rounded-lg p-4 text-on-surface outline-none transition-all placeholder:text-on-surface-variant/40"
                                placeholder="e.g. john@company.com"
                                type="email" />
                        </div>
                    </div>
                    <div className="space-y-3">
                        <label
                            htmlFor="contact-subject"
                            className="font-label text-xs tracking-widest text-primary/70 uppercase font-bold">Mission
                            Objective / Subject</label>
                        <input
                            id="contact-subject"
                            name="subject"
                            required
                            className="w-full bg-surface-container-lowest/50 border border-outline-variant/30 focus:border-primary focus:ring-1 focus:ring-primary rounded-lg p-4 text-on-surface outline-none transition-all placeholder:text-on-surface-variant/40"
                            placeholder="Project Inquiry"
                            type="text" />
                    </div>
                    <div className="space-y-3">
                        <label
                            htmlFor="contact-message"
                            className="font-label text-xs tracking-widest text-primary/70 uppercase font-bold">Message
                            Body</label>
                        <textarea
                            id="contact-message"
                            name="message"
                            required
                            className="w-full bg-surface-container-lowest/50 border border-outline-variant/30 focus:border-primary focus:ring-1 focus:ring-primary rounded-lg p-4 text-on-surface outline-none transition-all resize-none placeholder:text-on-surface-variant/40"
                            placeholder="Detail your technical requirements or proposal..."
                            rows="6"></textarea>
                    </div>
                    <button
                        className="w-full bg-primary text-on-primary font-label font-bold tracking-widest text-sm py-5 rounded-lg hover:brightness-110 transition-all flex items-center justify-center gap-3 group shadow-lg shadow-primary/20 disabled:opacity-60 disabled:cursor-not-allowed"
                        type="submit"
                        disabled={sending}>
                        {sending ? 'SENDING…' : 'ESTABLISH CONNECTION'}
                        <span
                            className="material-symbols-outlined group-hover:translate-x-2 transition-transform">send</span>
                    </button>
                </form>
            </section>
            {/*  Sidebar Info  */}
            <aside className="lg:col-span-5 space-y-gutter">
                {/*  Identity Card  */}
                <div className="glass-card p-10 rounded-xl border-t-4 border-primary">
                    <h3 className="font-label text-xs tracking-[0.3em] text-primary mb-8 font-black">DIRECT PROTOCOLS</h3>
                    <div className="space-y-6">
                        <div
                            className="flex items-start gap-5 p-5 bg-surface-container-high/30 rounded-xl group hover:bg-surface-container-high/50 transition-colors border border-outline-variant/10">
                            <div className="p-3 bg-secondary-container/50 rounded-lg">
                                <span className="material-symbols-outlined text-primary">mail</span>
                            </div>
                            <div className="overflow-hidden">
                                <p
                                    className="font-label text-[10px] tracking-widest text-on-surface-variant font-bold mb-1">
                                    EMAIL CONTACT</p>
                                <a className="font-body text-lg text-primary hover:underline transition-all block truncate"
                                    href={`mailto:${contact.email}`}>{contact.email}</a>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <a className="flex flex-col items-center justify-center p-8 bg-surface-container-high/30 rounded-xl group hover:bg-primary/10 transition-all border border-outline-variant/10 hover:border-primary/30"
                                href={contact.linkedin}
                                target="_blank"
                                rel="noopener noreferrer">
                                <span className="material-symbols-outlined text-primary text-3xl mb-3">share</span>
                                <span className="font-label text-xs font-bold tracking-widest uppercase">LinkedIn</span>
                            </a>
                            <a className="flex flex-col items-center justify-center p-8 bg-surface-container-high/30 rounded-xl group hover:bg-primary/10 transition-all border border-outline-variant/10 hover:border-primary/30"
                                href={contact.gitlab}
                                target="_blank"
                                rel="noopener noreferrer">
                                <span className="material-symbols-outlined text-primary text-3xl mb-3">code</span>
                                <span className="font-label text-xs font-bold tracking-widest uppercase">GitLab</span>
                            </a>
                        </div>
                    </div>
                </div>
                {/*  Location/Map Card  */}
                <div className="glass-card overflow-hidden rounded-xl relative min-h-[340px] flex flex-col">
                    <div className="h-56 relative overflow-hidden bg-surface-container-highest">
                        <img alt="Global Presence Map"
                            className="w-full h-full object-cover opacity-60 mix-blend-screen grayscale"
                            data-alt="A high-tech digital schematic map of the world rendered in neon blue lines on a dark navy background. The visualization features glowing nodes over the Middle East and South Asia, specifically highlighting Doha and India. The aesthetic is cyber-industrial and technical, mimicking a global server status dashboard with data streams and architectural overlays."
                            src="https://lh3.googleusercontent.com/aida-public/AB6AXuD0Zf6rE192KAFOJ2do0mWFxgqKImq4or7zbhiELO4kiWH99Q_U_8WkGM649qfNVKcdDEy8AucjIFSvLMm-cD5JQQ7PlJ667iZPIBvIogMFdofrkAmjTE3unm9-MW2XfGnm-ZDiBwld0STgu6fcTcoUVrrXsoTl7010x4hIlZRWwXaBiotSWUriNKX869FiUOx2be9CVMOLl9bYqtkKPstlTRyg2Aoirbpa3ytswC-_Ef6u-2dyEF4FDn8-GgmItpsalKqb3bOcNaw" />
                        <div
                            className="absolute inset-0 bg-gradient-to-t from-surface-container/90 via-surface-container/20 to-transparent">
                        </div>
                    </div>
                    <div className="p-10 mt-auto bg-surface-container/40">
                        <h3 className="font-label text-xs tracking-[0.3em] text-primary mb-6 font-black">DEPLOYMENT BASES
                        </h3>
                        <div className="flex flex-col gap-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="p-2 bg-primary/10 rounded-full">
                                        <span className="material-symbols-outlined text-primary text-sm"
                                            style={{ fontVariationSettings: "'FILL' 1" }}>location_on</span>
                                    </div>
                                    <span className="font-body font-semibold text-on-surface">Doha, Qatar</span>
                                </div>
                                <span
                                    className="font-label text-[10px] text-primary border border-primary/40 px-3 py-1 rounded-full font-bold">ACTIVE</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="p-2 bg-on-surface-variant/10 rounded-full">
                                        <span
                                            className="material-symbols-outlined text-on-surface-variant text-sm">home_pin</span>
                                    </div>
                                    <span className="font-body font-semibold text-on-surface-variant">India</span>
                                </div>
                                <span
                                    className="font-label text-[10px] text-on-surface-variant border border-outline-variant/40 px-3 py-1 rounded-full font-bold">NATIVE</span>
                            </div>
                        </div>
                    </div>
                </div>
            </aside>
        </div>
        {/*  Career Timeline Placeholder (Architectural element)  */}
        <div className="mt-stack-lg glass-card p-12 rounded-xl">
            <div className="flex items-center gap-6 mb-12">
                <div className="h-1 w-16 bg-primary rounded-full"></div>
                <h2 className="font-label text-sm text-on-surface font-black tracking-[0.4em] uppercase">Consultation
                    Workflow</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-gutter relative">
                <div className="hidden md:block absolute top-6 left-12 right-12 h-0.5 bg-outline-variant/20 z-0"></div>
                <div className="relative z-10 space-y-6 text-center md:text-left">
                    <div
                        className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center text-on-primary font-black text-xl shadow-lg shadow-primary/30 mx-auto md:mx-0">
                        01</div>
                    <div>
                        <h4 className="font-display font-bold text-primary mb-2 text-lg">DISCOVERY</h4>
                        <p className="font-body text-sm text-on-surface-variant leading-relaxed">Defining project scope and
                            technical requirements.</p>
                    </div>
                </div>
                <div className="relative z-10 space-y-6 text-center md:text-left">
                    <div
                        className="w-12 h-12 rounded-xl bg-surface-container-high border-2 border-primary flex items-center justify-center text-primary font-black text-xl mx-auto md:mx-0">
                        02</div>
                    <div>
                        <h4 className="font-display font-bold text-primary mb-2 text-lg">BLUEPRINT</h4>
                        <p className="font-body text-sm text-on-surface-variant leading-relaxed">Architecting the solution
                            and selecting the tech stack.</p>
                    </div>
                </div>
                <div className="relative z-10 space-y-6 text-center md:text-left">
                    <div
                        className="w-12 h-12 rounded-xl bg-surface-container-high border-2 border-primary flex items-center justify-center text-primary font-black text-xl mx-auto md:mx-0">
                        03</div>
                    <div>
                        <h4 className="font-display font-bold text-primary mb-2 text-lg">DEVELOPMENT</h4>
                        <p className="font-body text-sm text-on-surface-variant leading-relaxed">High-performance
                            engineering and iterative testing.</p>
                    </div>
                </div>
                <div className="relative z-10 space-y-6 text-center md:text-left">
                    <div
                        className="w-12 h-12 rounded-xl bg-surface-container-high border-2 border-primary flex items-center justify-center text-primary font-black text-xl mx-auto md:mx-0">
                        04</div>
                    <div>
                        <h4 className="font-display font-bold text-primary mb-2 text-lg">DEPLOYMENT</h4>
                        <p className="font-body text-sm text-on-surface-variant leading-relaxed">Optimization and launch of
                            the final architecture.</p>
                    </div>
                </div>
            </div>
        </div>
    </main>
  );
};

export default Contact;
